import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Sidebar.css";
import logo from "../assets/L1.png";
import {
  FaHome,
  FaInfoCircle,
  FaPhoneAlt,
  FaQuestionCircle,
  FaUser,
  FaCreditCard,
  FaHistory,
  FaMoneyBillWave,
  FaCog,
  FaUsers,
  FaCheckCircle,
  FaCashRegister,
  FaPlusCircle,
  FaChartLine,
  FaSignOutAlt,
  FaChevronRight,
  FaLock,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaClipboardCheck,
  FaMoneyCheckAlt
} from "react-icons/fa";

const Sidebar = ({ isLoggedIn, isAdmin, onCollapseChange }) => {
  const { handleLogout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    admin: true,
    user: true
  });
  const location = useLocation();

  useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(isCollapsed);
    }
  }, [isCollapsed, onCollapseChange]);

  // Close sidebar when location changes (navigation happens)
  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const closeSidebar = () => setIsOpen(false);
  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const guestNavItems = [
    { to: "/", label: "Home", icon: <FaHome /> },
    { to: "/about", label: "About", icon: <FaInfoCircle /> },
    { to: "/contact", label: "Contact", icon: <FaPhoneAlt /> },
    { to: "/faq", label: "FAQ", icon: <FaQuestionCircle /> },
    { to: "/login", label: "Login", icon: <FaLock /> },
    { to: "/register", label: "Register", icon: <FaUser /> }
  ];

  const userNavItems = [
    { to: "/dashboard", label: "Dashboard", icon: <FaChartLine /> },
    { to: "/loan-products", label: "Loan Products", icon: <FaCog /> },
    { to: "/loan-applications", label: "Loan Approvals", icon: <FaClipboardCheck /> },
    { to: "/apply-loan", label: "Apply Loan", icon: <FaCreditCard /> },
    { to: "/loan-history", label: "Loan History", icon: <FaHistory /> },
    { to: "/repayment", label: "Repayment", icon: <FaMoneyBillWave /> },
    { to: '/payment-allocation-history', label: 'Allocation History', icon: <FaMoneyCheckAlt /> },
    { to: "/profile", label: "Profile", icon: <FaUser /> }
  ];

  const adminNavItems = [
    { to: "/admin/dashboard", label: "Dashboard", icon: <FaChartLine /> },
    { to: "/admin/loanProducts", label: "Products", icon: <FaCog /> },
    { to: "/admin/users", label: "Users", icon: <FaUsers /> },
     { to: "/admin/approvals", label: "Approvals", icon: <FaCheckCircle /> },
    { to: "/admin/loans", label: "Loans", icon: <FaCreditCard /> },
    //  { to: "/admin/addLoans", label: "Add Loans", icon: <FaPlusCircle /> },
    { to: "/admin/makePayments", label: "Payments", icon: <FaCashRegister /> },
    { to: "/admin/loanPayments", label: "Payment History", icon: <FaHistory /> }
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavItemClick = (onClick) => {
    // Close sidebar and allow React Router to handle navigation
    if (onClick) {
      onClick();
    }
  };

  const NavItem = ({ to, label, icon, onClick, isActive, isCollapsed }) => (
    <Link
      to={to}
      onClick={() => handleNavItemClick(onClick)}
      className={`nav-link relative flex items-center px-4 py-3 transition-all duration-200 ${
        isActive 
          ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600 font-medium'
          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
      } ${isCollapsed ? 'justify-center' : ''}`}
      title={isCollapsed ? label : ''}
    >
      <div className="flex items-center">
        <span className={`${isActive ? 'text-blue-600' : 'text-gray-300'} ${isCollapsed ? '' : 'mr-3'}`}>
          {React.cloneElement(icon, { className: "w-5 h-5" })}
        </span>
        {!isCollapsed && (
          <span className="text-sm">{label}</span>
        )}
      </div>
      {isActive && !isCollapsed && (
        <div className="absolute right-3">
          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
        </div>
      )}
    </Link>
  );

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        className="fixed top-4 left-4 z-50 w-10 h-10 bg-white rounded-lg shadow-md flex items-center justify-center md:hidden"
        onClick={toggleSidebar}
      >
        {isOpen ? (
          <FaTimes className="w-5 h-5 text-gray-700" />
        ) : (
          <FaBars className="w-5 h-5 text-gray-700" />
        )}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`sidebar fixed top-0 left-0 h-screen z-50 bg-gray-900 shadow-xl flex flex-col ${
          isOpen ? "open" : ""
        } ${isCollapsed ? "collapsed" : ""}`}
        style={{
          width: isCollapsed ? '80px' : '260px',
          borderRight: '1px solid #e5e7eb'
        }}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <img 
                  src={logo} 
                  alt="Smart Lend" 
                  className="w-8 h-8"
                />
              </div>
              {!isCollapsed && (
                <div>
                  <h2 className="text-xl font-extrabold text-white tracking-tight drop-shadow-md">SMART LEND</h2>
                  <p className="text-xs text-gray-300">Digital Lending</p>
                </div>
              )}
            </div>
            
            {/* Desktop Collapse Toggle removed per request */}
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav flex-1 px-2 py-4 overflow-y-auto">
          {!isLoggedIn ? (
            <div className="space-y-1">
              {guestNavItems.map((item) => (
                <NavItem
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  onClick={closeSidebar}
                  isActive={isActive(item.to)}
                  isCollapsed={isCollapsed}
                />
              ))}
            </div>
          ) : !isAdmin ? (
            <>
              {!isCollapsed && (
                <div className="px-4 py-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    My Account
                  </h3>
                </div>
              )}
              <div className="space-y-1">
                {userNavItems.map((item) => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    icon={item.icon}
                    onClick={closeSidebar}
                    isActive={isActive(item.to)}
                    isCollapsed={isCollapsed}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              {!isCollapsed && (
                <div className="px-4 py-2 flex items-center justify-between">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Admin Panel
                  </h3>
                  <button
                    onClick={() => toggleSection('admin')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {expandedSections.admin ? (
                      <FaChevronUp className="w-3 h-3" />
                    ) : (
                      <FaChevronDown className="w-3 h-3" />
                    )}
                  </button>
                </div>
              )}
              <div className={`space-y-1 ${expandedSections.admin || isCollapsed ? '' : 'hidden'}`}>
                {adminNavItems.map((item) => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    icon={item.icon}
                    onClick={closeSidebar}
                    isActive={isActive(item.to)}
                    isCollapsed={isCollapsed}
                  />
                ))}
              </div>
            </>
          )}
        </nav>

        {/* Footer Section */}
        <div className="border-t border-gray-100 p-4">
          {isLoggedIn ? (
            <>
              <div className={`mb-3 ${isCollapsed ? 'text-center' : ''}`}>
                <button
                  onClick={() => { handleLogout(); closeSidebar(); }}
                  className={`flex bg-red-500 text-white items-center justify-center w-full py-2.5 rounded-lg  transition-colors ${
                    isCollapsed 
                      ? 'px-2 text-white hover:bg-white hover:text-red-600  border-red-100'
                      : 'px-4 text-gray-700 hover:text-red-600 hover:border-red-200 hover:bg-white-50 border-gray-200'
                  }`}
                  title={isCollapsed ? "Logout" : ""}
                >
                  <FaSignOutAlt className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4 mr-2'}`} />
                  {!isCollapsed && <span className="text-sm font-medium">Logout</span>}
                </button>
              </div>
              
              {/* Collapse Toggle - Desktop Bottom */}
              {isCollapsed ? (
                <button
                  onClick={toggleCollapse}
                  className="w-full py-2 flex items-center justify-center text-gray-500 hover:text-gray-700"
                  title="Expand sidebar"
                >
                  <FaChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>v2.1.0</span>
                  <span>© 2024</span>
                </div>
              )}
            </>
          ) : !isCollapsed && (
            <div className="text-center">
              <p className="text-xs text-gray-500">Ready to get started?</p>
              <Link
                to="/register"
                onClick={closeSidebar}
                className="inline-block mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Create Account →
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;